import React, { useEffect, useRef } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, Typography, Button, Card, CardContent, useMediaQuery } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentIcon from '@mui/icons-material/Payment';
import ValueCalculator from './components/ValueCalculator';
import TransformationRequest from './components/TransformationRequest';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MahalleStats from './components/MahalleStats';

const theme = createTheme({
  palette: {
    primary: {
      main: 'rgb(248, 153, 49)',
      light: 'rgb(250, 173, 89)',
      dark: 'rgb(198, 123, 39)',
    },
    secondary: {
      main: '#424242',
      light: '#616161',
      dark: '#212121',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        },
      },
    },
  },
});

function App() {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  const setRef = (index: number) => (el: HTMLDivElement | null) => {
    sectionsRef.current[index] = el;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ bgcolor: '#f4fafc', minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(248, 153, 49, 0.85), rgba(66, 66, 66, 0.85)), url("/images/Anasayfa.jpg")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed',
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box 
              component="img"
              src="/images/logo1.png"
              alt="Esenler Kentsel Dönüşüm Logo"
              sx={{
                width: 'auto',
                height: '140px',
                filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.8))',
                display: 'block',
                position: 'absolute',
                top: '-200px',
                left: '24px',
                zIndex: 3,
                '@media (max-width:600px)': {
                  height: '80px',
                  top: '-120px',
                  left: '16px',
                },
              }}
            />
            <Box sx={{ 
              maxWidth: '800px', 
              color: 'white',
              mt: 15,
              animation: 'fadeIn 1s ease-in',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                '&::after': {
                  content: '""',
                  display: 'block',
                  width: '60px',
                  height: '4px',
                  background: 'rgb(248, 153, 49)',
                  marginTop: '10px',
                },
              }}>
                <Typography 
                  variant="h1" 
                  sx={{
                    color: 'white',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                  }}
                >
                  Esenler'de Yeni Bir Başlangıç
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 4,
                  fontWeight: 500,
                  opacity: 0.9,
                  lineHeight: 1.4,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                }}
              >
                Güvenli ve değerli bir gelecek için fikrin değişsin,evin gelişsin
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                flexWrap: 'wrap',
                '& button': {
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  },
                },
              }}>
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={() => {
                    document.getElementById('transformation-request')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    background: '#424242',
                    color: 'white',
                    padding: '12px 32px',
                    fontSize: '1.1rem',
                    '&:hover': {
                      background: '#616161',
                    },
                  }}
                >
                  Hemen Başvur
                </Button>
                <Button 
                  variant="outlined" 
                  color="inherit"
                  size="large"
                  onClick={() => {
                    document.getElementById('value-calculator')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    borderWidth: 2,
                    borderColor: 'white',
                    color: 'white',
                    padding: '12px 32px',
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Değer Hesapla
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Value Calculator Section */}
        <Box 
          ref={setRef(0)}
          id="value-calculator" 
          sx={{ 
            py: 12, 
            bgcolor: 'background.default',
            position: 'relative',
            opacity: 0,
            transform: 'translateY(50px)',
            transition: 'all 0.8s ease-out',
            '&.animate-in': {
              opacity: 1,
              transform: 'translateY(0)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100px',
              background: 'linear-gradient(to bottom, rgba(248, 153, 49, 0.1), transparent)',
              zIndex: 0,
            },
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              variant="h1" 
              align="center" 
              gutterBottom
              sx={{ 
                mb: 4, 
                color: 'rgb(248, 153, 49)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  display: 'block',
                  width: '80px',
                  height: '4px',
                  background: 'rgb(248, 153, 49)',
                  margin: '20px auto 0',
                  borderRadius: '2px',
                },
              }}
            >
              Değer Hesaplama Aracı
            </Typography>
            <Box sx={{
              animation: 'slideUp 0.8s ease-out',
              '@keyframes slideUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(40px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}>
              <ValueCalculator />
            </Box>
          </Container>
        </Box>

        {/* Mahalle Stats Section */}
        <Box 
          ref={setRef(1)}
          id="mahalle-stats" 
          sx={{ 
            py: 12, 
            bgcolor: 'background.paper',
            position: 'relative',
            opacity: 0,
            transform: 'translateY(50px)',
            transition: 'all 0.8s ease-out',
            '&.animate-in': {
              opacity: 1,
              transform: 'translateY(0)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100px',
              background: 'linear-gradient(to bottom, rgba(66, 66, 66, 0.1), transparent)',
              zIndex: 0,
            },
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              variant="h1" 
              align="center" 
              gutterBottom
              sx={{ 
                mb: 4, 
                color: 'rgb(248, 153, 49)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  display: 'block',
                  width: '80px',
                  height: '4px',
                  background: 'rgb(248, 153, 49)',
                  margin: '20px auto 0',
                  borderRadius: '2px',
                },
              }}
            >
              Mahalle İstatistikleri
            </Typography>
            <Box sx={{
              animation: 'slideUp 0.8s ease-out',
              '@keyframes slideUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(40px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}>
              <MahalleStats />
            </Box>
          </Container>
        </Box>

        {/* Transformation Request Section */}
        <Box 
          ref={setRef(2)}
          id="transformation-request" 
          sx={{ 
            py: 12, 
            bgcolor: 'background.paper',
            position: 'relative',
            opacity: 0,
            transform: 'translateY(50px)',
            transition: 'all 0.8s ease-out',
            '&.animate-in': {
              opacity: 1,
              transform: 'translateY(0)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100px',
              background: 'linear-gradient(to bottom, rgba(248, 153, 49, 0.1), transparent)',
              zIndex: 0,
            },
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              variant="h1" 
              align="center" 
              gutterBottom
              sx={{ 
                mb: 4, 
                color: 'rgb(248, 153, 49)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  display: 'block',
                  width: '80px',
                  height: '4px',
                  background: 'rgb(248, 153, 49)',
                  margin: '20px auto 0',
                  borderRadius: '2px',
                },
              }}
            >
              Kentsel Dönüşüm İstek Formu
            </Typography>
            <Box sx={{
              animation: 'slideUp 0.8s ease-out',
              '@keyframes slideUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(40px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}>
              <TransformationRequest />
            </Box>
          </Container>
        </Box>

        {/* FAQ Section */}
        <Box 
          ref={setRef(3)}
          sx={{ 
            py: 12, 
            bgcolor: 'background.default',
            position: 'relative',
            opacity: 0,
            transform: 'translateY(50px)',
            transition: 'all 0.8s ease-out',
            '&.animate-in': {
              opacity: 1,
              transform: 'translateY(0)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100px',
              background: 'linear-gradient(to bottom, rgba(66, 66, 66, 0.1), transparent)',
              zIndex: 0,
            },
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              variant="h1" 
              align="center" 
              gutterBottom
              sx={{ 
                mb: 4, 
                color: 'rgb(248, 153, 49)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  display: 'block',
                  width: '80px',
                  height: '4px',
                  background: 'rgb(248, 153, 49)',
                  margin: '20px auto 0',
                  borderRadius: '2px',
                },
              }}
            >
              Sık Sorulan Sorular
            </Typography>
            <Box sx={{ 
              maxWidth: 1200, 
              mx: 'auto',
              animation: 'slideUp 0.8s ease-out',
              '@keyframes slideUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(40px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}>
              <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Kentsel dönüşüm süreci ne kadar sürer?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Kentsel dönüşüm süreci, projenin büyüklüğüne ve katılımcı sayısına göre değişmekle birlikte, ortalama 18-24 ay sürmektedir. Bu süreç içinde:
                      • İlk 3 ay: Proje planlaması ve onay süreci
                      • 3-6 ay: Yıkım ve temizlik işlemleri
                      • 6-18 ay: İnşaat süreci
                      • 18-24 ay: Teslim ve taşınma süreci
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Kira desteği nasıl alınır?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Kira desteği için Esenler Belediyesi'ne başvuru yapmanız gerekmektedir. Başvurunuz değerlendirildikten sonra, uygun görülmeniz halinde:
                      • 18 ay boyunca aylık 1.500 TL kira desteği
                      • 3.000 TL taşınma gideri desteği
                      • Aylık 500 TL fatura desteği
                      almaya başlayabilirsiniz.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Neden kentsel dönüşümden vazgeçmemeliyim?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Kentsel dönüşümden vazgeçmemeniz için önemli nedenler:
                      • Deprem güvenliği: Yeni binalar en son deprem yönetmeliğine uygun
                      • Değer artışı: Esenler'in gelişen bölgelerinde değer artışı
                      • Modern yaşam: Yeni sosyal tesisler ve yeşil alanlar
                      • Devlet desteği: Kira ve taşınma yardımları
                      • Kolay ödeme: Uygun ödeme seçenekleri
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Hangi devlet desteklerinden yararlanabilirim?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Kentsel dönüşüm sürecinde yararlanabileceğiniz devlet destekleri:
                      • 30.000 TL'ye varan kira yardımı (18 ay boyunca aylık 1.500 TL)
                      • 3.000 TL taşınma yardımı
                      • 500 TL aylık fatura desteği
                      • 50.000 TL'ye varan kredi desteği (faizsiz)
                      • Tapu harcı muafiyeti
                      • Yapı ruhsatı ve yapı kullanma izni harç muafiyeti
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Yeni binanın deprem güvenliği nasıl sağlanıyor?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Yeni binalarımızda deprem güvenliği şu şekilde sağlanmaktadır:
                      • En son deprem yönetmeliğine uygun tasarım
                      • Yüksek kaliteli beton ve çelik kullanımı
                      • Sismik izolatörler ve enerji sönümleyiciler
                      • Düzenli denetim ve kalite kontrol
                      • 3. parti bağımsız denetim firmaları tarafından kontrol
                      • Tüm yapı elemanlarının test ve sertifikasyonu
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Ödeme seçenekleri nelerdir?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Size özel esnek ödeme seçenekleri sunuyoruz:
                      • Peşin ödeme avantajı
                      • 12-24-36 ay vadeli ödeme seçenekleri
                      • Banka kredisi ile ödeme imkanı
                      • Devlet destekli kredi kullanımı
                      • Kira ödemesi ile birlikte taksitlendirme
                      • Özel kampanya ve indirim fırsatları
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Yeni binada hangi sosyal tesisler bulunuyor?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Yeni projemizde bulunan sosyal tesisler:
                      • Kapalı ve açık otopark
                      • Fitness merkezi
                      • Yüzme havuzu
                      • Çocuk oyun alanı
                      • Sosyal aktivite alanları
                      • Yeşil alan ve peyzaj düzenlemesi
                      • 7/24 güvenlik ve kamera sistemi
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ 
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  border: '1px solid',
                  borderColor: 'rgb(248, 153, 49)',
                  borderRadius: '8px !important',
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgb(248, 153, 49)' }} />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(248, 153, 49, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#424242' }}>
                      Başvuru süreci nasıl işliyor?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      Başvuru sürecimiz şu şekilde işlemektedir:
                      • Ücretsiz keşif ve değerleme
                      • Detaylı bilgilendirme toplantısı
                      • Başvuru formunun doldurulması
                      • Gerekli belgelerin teslimi
                      • Sözleşme imzalama
                      • Devlet destekleri için başvuru
                      • Süreç takibi ve bilgilendirme
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Footer */}
        <Box 
          ref={setRef(4)}
          sx={{ 
            bgcolor: '#424242',
            color: 'white',
            py: 8,
            opacity: 0,
            transform: 'translateY(50px)',
            transition: 'all 0.8s ease-out',
            '&.animate-in': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
              gap: 4 
            }}>
              <Box sx={{ height: '100%', minHeight: '200px' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'rgb(248, 153, 49)' }}>
                  Esenler Kentsel Dönüşüm
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Güvenli ve değerli bir gelecek için yanınızdayız.
                </Typography>
              </Box>
              <Box sx={{ height: '100%', minHeight: '200px' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'rgb(248, 153, 49)' }}>
                  İletişim
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Adres: Esenler, İstanbul
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Tel: (0212) XXX XX XX
                </Typography>
              </Box>
              <Box sx={{ height: '100%', minHeight: '200px' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'rgb(248, 153, 49)' }}>
                  Sosyal Medya
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Bizi sosyal medyada takip edin
                </Typography>
              </Box>
            </Box>
            <Typography 
              variant="body2" 
              align="center" 
              sx={{ 
                mt: 8, 
                pt: 4, 
                borderTop: '1px solid rgba(248, 153, 49, 0.2)',
                opacity: 0.8,
              }}
            >
              © 2024 Esenler Kentsel Dönüşüm. Tüm hakları saklıdır.
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
